import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import '../styles/channels.css';
import { currentLocale, t } from '../lib/i18n';
import {
  CAPABILITY,
  Channel,
  CHANNEL_LABEL,
  countryName,
  eur,
  EVENT_LABEL,
  eventTone,
  msText,
  Notification,
  NOTIFICATION_STATUS,
  NotificationStatus,
  NotificationStep,
  PROVIDER_LABEL,
  seconds,
  SIGNAL_HINT,
  SKIP_REASON,
  STEP_STATE,
  STOP_REASON,
  TimelineItem,
} from './model';

/**
 * Display pieces of Ocean Channels without any API or session dependency: shared by the dashboard and «Маяк»
 * (apps/admin imports them through the `@web/channels` alias).
 */

// ---------- icons (one wave family; currentColor) ----------

const PATHS: Record<Channel, ReactNode> = {
  email: <><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 6 8-6" /></>,
  sms: <><path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-9l-5 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" /><path d="M8 10h.01M12 10h.01M16 10h.01" /></>,
  whatsapp: <><path d="M4.5 19.5 5.6 16A8 8 0 1 1 8 18.4Z" /><path d="M9.5 9.2c0 2.7 2.6 5.3 5.3 5.3l1-1.2-1.6-.9-.8.6a4 4 0 0 1-2.4-2.4l.6-.8-.9-1.6Z" /></>,
  telegram_bot: <><path d="m3.5 11.2 16-6.2-2.6 14-5.2-4.3" /><path d="m11.7 14.7-2.2 4 .4-5.2 7-6.4" /></>,
  telegram_gateway: <><path d="m3.5 11.2 16-6.2-2.6 14-5.2-4.3" /><rect x="14" y="14" width="7" height="6" rx="1.2" /><path d="M15.5 14v-1.5a2 2 0 0 1 4 0V14" /></>,
  push: <><rect x="7" y="2.5" width="10" height="19" rx="2.5" /><path d="M11 18.5h2" /><circle cx="16.5" cy="5.5" r="2.2" fill="currentColor" stroke="none" /></>,
  web_push: <><rect x="2.5" y="4" width="19" height="14" rx="2" /><path d="M2.5 8h19M8 21h8" /><circle cx="18" cy="11.5" r="2" fill="currentColor" stroke="none" /></>,
};

export function ChannelIcon({ channel, size = 18 }: { channel: Channel; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {PATHS[channel]}
    </svg>
  );
}

/** «не проверено на живом аккаунте» (§5): every adapter except Web Push in this build. */
export function LiveBadge({ live, provider }: { live: boolean; provider?: string }) {
  if (provider === 'fake') return <span className="pill cancelled" title={t('Тестовый поставщик: сообщения никуда не уходят')}>{t('тестовый')}</span>;
  if (live) return <span className="pill ok" title={t('Проверено от начала до конца')}>{t('проверено')}</span>;
  return <span className="pill warn" title={t('Адаптер написан по официальной документации поставщика, но ещё не проверен на настоящем аккаунте')}>{t('не проверено на живом аккаунте')}</span>;
}

export function NotificationPill({ status }: { status: NotificationStatus }) {
  const s = NOTIFICATION_STATUS[status] ?? { label: status, tone: '', hint: '' };
  return <span className={`pill ${s.tone}`} title={t(s.hint)}>{t(s.label)}</span>;
}

export function StepPill({ state }: { state: string }) {
  const s = STEP_STATE[state] ?? { label: state, tone: '' };
  return <span className={`pill ${s.tone}`}>{t(s.label)}</span>;
}

export function ChannelChip({ channel }: { channel: Channel }) {
  return (
    <span className={`ch-chip ch-${channel}`}>
      <ChannelIcon channel={channel} size={14} /> {t(CHANNEL_LABEL[channel])}
    </span>
  );
}

/** Four honest columns plus cost: never one «доставлено» number. */
export function SignalColumns({ s, channel }: { s: { sent: number; accepted: number; delivered: number; read: number; unknown?: number; cost?: { amount: string | null } }; channel: Channel }) {
  const cap = CAPABILITY[channel];
  const cell = (label: string, v: number, available: boolean, hint: string) => (
    <div className={`sig${available ? '' : ' off'}`} title={available ? t(hint) : t('{channel} не сообщает этот сигнал', { channel: t(CHANNEL_LABEL[channel]) })}>
      <b>{available ? v.toLocaleString(currentLocale()) : '—'}</b>
      <span>{t(label)}</span>
    </div>
  );
  return (
    <div className="sig-row">
      {cell('отправлено', s.sent, true, 'Передано поставщику')}
      {cell('принято', s.accepted, true, SIGNAL_HINT.accepted)}
      {cell('доставлено', s.delivered, cap !== 'accepted', SIGNAL_HINT.delivered)}
      {cell('прочитано', s.read, cap === 'read', SIGNAL_HINT.read)}
      {s.cost && (
        <div className="sig money" title={t('За 7 дней: факт поставщика, до его прихода — оценка')}>
          <b>{eur(s.cost.amount, 2)}</b>
          <span>{t('стоимость')}</span>
        </div>
      )}
    </div>
  );
}

// ---------- «Погружение уведомления» ----------

/** The stop banner of a cascade stopped by an unknown outcome — and what a reconciliation later established. */
function unknownStopText(n: Notification): string {
  const open = n.steps.find((s) => s.state === 'unknown');
  if (open) return t('Шаг {seq} — неизвестный исход: дальше не шлём, чтобы не продублировать.', { seq: open.seq });
  const rec = n.steps.find((s) => s.reconciled_at);
  if (rec) return t('Исход шага {seq} был неизвестен; сотрудник MailOcean сверил его вручную ({result}). Каскад не возобновляется.', { seq: rec.seq, result: rec.state === 'accepted' ? t('принято поставщиком') : t('не принято') });
  return t('Шаг {seq} — неизвестный исход: дальше не шлём, чтобы не продублировать.', { seq: n.current_seq });
}

function stepTimes(s: NotificationStep): [string, number | null][] {
  return [
    ['в очереди', s.times_ms.started],
    ['передан', s.times_ms.handed],
    ['принят', s.times_ms.accepted],
    ['доставлен', s.times_ms.delivered],
    ['прочитан', s.times_ms.read],
  ].filter(([, v]) => v != null) as [string, number | null][];
}

function eventLine(e: TimelineItem): string {
  const d = e.data ?? {};
  const bits: string[] = [];
  if (typeof d.code === 'string') bits.push(t('код {code}', { code: d.code }));
  if (typeof d.reason === 'string') bits.push(t(SKIP_REASON[d.reason] ?? d.reason));
  if (typeof d.provider === 'string') bits.push(t(PROVIDER_LABEL[d.provider] ?? d.provider));
  if (typeof d.attempt === 'number') bits.push(t('попытка {n}', { n: d.attempt }));
  if (typeof d.country === 'string') bits.push(countryName(d.country));
  if (typeof d.segments === 'number') bits.push(t('{n} сегм.', { n: d.segments }));
  if (typeof d.via === 'string') bits.push(t('через {via}', { via: d.via }));
  if (typeof d.error === 'string') bits.push(d.error);
  return bits.join(' · ');
}

/**
 * The dive of a notification: the cascade top-down, one depth per step, with skip reasons in words, millisecond
 * times from creation, estimate → actual cost, the link to the email of an email step and why a stopped cascade
 * stopped. Parallel steps go to the side.
 */
export function NotificationDive({ n, emailLink = true }: { n: Notification; emailLink?: boolean }) {
  const events = n.timeline ?? [];
  const cascade = n.steps.filter((s) => !s.parallel);
  const parallel = n.steps.filter((s) => s.parallel);
  const general = events.filter((e) => e.seq == null);
  const card = (s: NotificationStep, i: number) => {
    const mine = events.filter((e) => e.seq === s.seq);
    const current = n.status === 'running' && s.seq === n.current_seq;
    const won = n.succeeded_seq === s.seq;
    return (
      <li key={s.seq} className={`ndive-step st-${s.state}${current ? ' current' : ''}${won ? ' won' : ''}`} style={{ animationDelay: `${i * 70}ms` }}>
        <div className="ndive-head">
          <span className="ndive-depth">{s.parallel ? t('параллельно') : t('шаг {seq}', { seq: s.seq })}</span>
          <ChannelChip channel={s.channel} />
          <StepPill state={s.state} />
          {won && <span className="pill ok">{t('успех здесь')}</span>}
          <span className="spacer" />
          <span className="ndive-cost" title={s.cost != null ? t('Факт по данным поставщика') : t('Оценка до отчёта поставщика')}>
            {['skipped', 'failed', 'cancelled'].includes(s.state) && s.cost == null ? <>{t('не оплачивается')}</> : s.cost != null ? <>{t('факт {sum}', { sum: eur(s.cost) })}</> : <>{t('оценка {sum}', { sum: eur(s.estimated_cost) })}</>}
            {s.paid_by === 'tenant' && <small> · {t('ваш аккаунт')}</small>}
          </span>
        </div>
        <div className="ndive-meta">
          {s.provider && <span>{t(PROVIDER_LABEL[s.provider] ?? s.provider)}</span>}
          {s.country && <span>{countryName(s.country)}</span>}
          {s.segments != null && s.channel === 'sms' && <span>{t('{n} сегм.', { n: s.segments })}</span>}
          {s.deadline_seconds != null && <span>{t('дедлайн {n}', { n: seconds(s.deadline_seconds) })}</span>}
          {s.email_id && emailLink && <Link to={`/emails/${s.email_id}`}>{t('письмо {id}…', { id: s.email_id.slice(0, 12) })}</Link>}
        </div>
        {s.skip_reason && <div className="ndive-why">{t('Пропущен: {reason}.', { reason: t(SKIP_REASON[s.skip_reason] ?? s.skip_reason) })}</div>}
        {s.reconciled_at && (
          <div className="ndive-why">
            {t('Сверено сотрудником MailOcean вручную: {result}. Время приёма у поставщика неизвестно; каскад не возобновляется.', { result: s.state === 'accepted' ? t('поставщик принял сообщение') : t('поставщик сообщение не принял') })}
          </div>
        )}
        {s.fail_code && !s.reconciled_at && <div className="ndive-why bad">{t('Отказ поставщика:')} <code>{s.fail_code}</code>{s.state === 'failed' ? t(' — точный отказ, можно идти дальше.') : ''}</div>}
        {s.state === 'unknown' && (
          <div className="ndive-why warn">{t('Неизвестный исход: поставщик мог принять сообщение. Повтора не будет')}{n.kind === 'verify' ? t('; у кода подтверждения следующий канал получает тот же код.') : t(' — дальше не шлём, чтобы не продублировать.')}</div>
        )}
        {stepTimes(s).length > 0 && (
          <div className="ndive-times">
            {stepTimes(s).map(([k, v]) => (
              <span key={k}>
                {t(k)} <b>+{msText(v)}</b>
              </span>
            ))}
          </div>
        )}
        {mine.length > 0 && (
          <details className="ndive-events">
            <summary>{t('события шага · {n}', { n: mine.length })}</summary>
            <ol>
              {mine.map((e, j) => (
                <li key={j} className={eventTone(e.type)}>
                  <span className="t">{t(EVENT_LABEL[e.type] ?? e.type)}</span>
                  <span className="ms">+{msText(e.elapsed_ms)}</span>
                  {eventLine(e) && <span className="m">{eventLine(e)}</span>}
                </li>
              ))}
            </ol>
          </details>
        )}
      </li>
    );
  };
  return (
    <div className="ndive">
      {n.status === 'stopped' && n.stop_reason && (
        <div className="callout warn">
          <div>
            <b>{t('Каскад остановлен.')}</b> {n.stop_reason === 'unknown' ? unknownStopText(n) : t(STOP_REASON[n.stop_reason] ?? n.stop_reason)}
          </div>
        </div>
      )}
      {n.status === 'failed' && <div className="callout bad"><div><b>{t('Не отправлено ни в один канал.')}</b> {t('Каждый шаг пропущен или получил точный отказ — причины ниже.')}</div></div>}
      {n.status === 'expired' && <div className="callout"><div><b>{t('Срок кода истёк.')}</b> {t('Код не ввели вовремя; запечатанная копия кода удалена.')}</div></div>}
      {n.status === 'cancelled' && <div className="callout"><div><b>{t('Отменено.')}</b> {t('Шаги, ещё не переданные поставщику, отменены; переданные не отзываются.')}</div></div>}
      <div className="ndive-body">
        <ol className="ndive-line">{cascade.map(card)}</ol>
        {parallel.length > 0 && (
          <div className="ndive-side">
            <div className="small muted">{t('Параллельно с каскадом (не отменяются его успехом)')}</div>
            <ol className="ndive-line side">{parallel.map(card)}</ol>
          </div>
        )}
      </div>
      {general.length > 0 && (
        <details className="ndive-events general">
          <summary>{t('события уведомления · {n}', { n: general.length })}</summary>
          <ol>
            {general.map((e, j) => (
              <li key={j} className={eventTone(e.type)}>
                <span className="t">{t(EVENT_LABEL[e.type] ?? e.type)}</span>
                <span className="ms">+{msText(e.elapsed_ms)}</span>
                {eventLine(e) && <span className="m">{eventLine(e)}</span>}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

